// The property rail: a record's values read as text and turn into the control that fits them on
// click or focus. Choices from a list are ui/Select (inline), short sets ui/SegTrack, and dates and
// numbers the kit's recessed field. The pieces here are the frame and the two plain editors.

import React, { useEffect, useRef, useState } from 'react';
import { IconPencil } from './icons';
import { CONTROL_MOTION, FOCUS, STATE, TOUCH } from './motion';
import Field from './Field';

/** A value that reads as text and is a control underneath: the kit's inline recipe. */
export const INLINE_VALUE = `-mx-1 inline-flex min-h-6 max-w-full items-center gap-1.5 rounded-inner px-1 text-left ${STATE} ${CONTROL_MOTION} ${FOCUS} ${TOUCH}`;

let seq = 0;

const ymdValue = (v: string | null | undefined): string => (v ? v.slice(0, 10) : '');

export const PropGroup: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <h3 className="m-0 mb-1 text-xs font-semibold leading-4 text-on-surface-variant">{title}</h3>
    {children}
  </div>
);

/** `wide` puts the control on its own line under the label, for a control too wide for the rail. */
export const PropRow: React.FC<{ label: string; wide?: boolean; children: React.ReactNode }> = ({ label, wide, children }) => (
  <div className="grid grid-cols-[5.25rem_minmax(0,1fr)] items-start gap-x-2">
    <span className={`truncate py-1 text-[13px] font-medium leading-6 text-on-surface-variant ${wide ? 'col-span-2' : ''}`}>{label}</span>
    <div className={`flex min-w-0 flex-wrap content-start items-center gap-x-2 gap-y-0.5 py-1 ${wide ? 'col-span-2 -mt-0.5' : ''}`}>
      {children}
    </div>
  </div>
);

/**
 * A date that reads as text. Enter or blur saves, Escape cancels. The caller draws the reading
 * (`readout`) and its spoken form (`speak`), so the wording around "due" stays the page's own.
 * `note` is spoken, never drawn. `readOnlyAfter` sits beside the reading when it cannot be edited.
 */
export const DateValue: React.FC<{
  label: string;
  value: string | null;
  editable: boolean;
  readout: (value: string | null) => React.ReactNode;
  speak?: (value: string) => string;
  note?: string;
  readOnlyAfter?: React.ReactNode;
  onSave: (ymd: string | null) => void;
}> = ({ label, value, editable, readout, speak = ymdValue, note, readOnlyAfter, onSave }) => {
  const [editing, setEditing] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const settled = useRef(false);
  const giveBack = useRef(false);
  const [noteId] = useState(() => `pr-note-${++seq}`);

  useEffect(() => {
    if (editing) { settled.current = false; input.current?.focus(); }
    else if (giveBack.current) { giveBack.current = false; button.current?.focus(); }
  }, [editing]);

  const finish = (save: boolean, refocus: boolean) => {
    if (settled.current) return;
    settled.current = true;
    giveBack.current = refocus;
    const v = input.current?.value ?? '';
    setEditing(false);
    if (save && v !== ymdValue(value)) onSave(v || null);
  };

  if (!editable) {
    return (
      <>
        {readout(value)}
        {readOnlyAfter}
      </>
    );
  }

  if (editing) {
    return (
      <Field
        ref={input}
        size="sm"
        type="date"
        aria-label={label}
        aria-describedby={note ? noteId : undefined}
        defaultValue={ymdValue(value)}
        // A day picked in the popup saves at once. Typing a year passes through 0002, 0020,
        // 0202 on the way to 2027, so only a plausible year counts as finished.
        onChange={(e) => { const v = e.target.value; if (v && Number(v.slice(0, 4)) >= 2000) finish(true, false); }}
        onBlur={() => finish(true, false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); finish(true, true); }
          else if (e.key === 'Escape') { e.preventDefault(); finish(false, true); }
        }}
        className="[color-scheme:dark] motion-safe:animate-menuIn"
      />
    );
  }

  return (
    <>
      <button
        ref={button}
        type="button"
        aria-describedby={note ? noteId : undefined}
        aria-label={`${label}, ${value ? speak(value) : 'not set'}, edit`}
        onClick={() => setEditing(true)}
        className={`group ${INLINE_VALUE}`}
      >
        {readout(value)}
        <IconPencil className="h-3.5 w-3.5 flex-none text-on-surface-variant opacity-60 transition-opacity duration-quick ease-emphasized group-hover:opacity-100" />
      </button>
      {note && <span id={noteId} className="sr-only">{note}</span>}
    </>
  );
};

/** Quiet text that is the well underneath: blur or Enter saves, Escape puts the old value back. */
export const InlineText: React.FC<{
  label: string;
  value: string;
  placeholder: string;
  maxLength: number;
  onSave: (v: string) => void;
}> = ({ label, value, placeholder, maxLength, onSave }) => (
  <Field
    key={value}
    variant="inline"
    aria-label={label}
    defaultValue={value}
    maxLength={maxLength}
    placeholder={placeholder}
    onBlur={(e) => { const v = e.target.value.trim(); if (v !== value) onSave(v); }}
    onKeyDown={(e) => {
      if (e.key === 'Enter') e.currentTarget.blur();
      else if (e.key === 'Escape') { e.currentTarget.value = value; e.currentTarget.blur(); }
    }}
  />
);
